import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';
import { makeCustomOption } from '@utils/makeCustomOption';

import { makeValueFromSelected, resolvePlaceholder } from '../../utils/selectHelpers';
import type { OptionType } from '../../Select';

interface Params {
    options: OptionType[];
    value: OptionType[] | undefined;
    selectedValues: string[] | number[] | undefined;
    customValues: string[] | undefined;
    placeholder: string | undefined;
    title: string | undefined;
    forcePlaceholder: boolean | undefined;
    name: string | undefined;
    onChange?(selectedItem: Nullable<OptionType[]>, name?: string): void;
    onCreateOption?(value: OptionType[], name?: string): void;
}

interface Data {
    isOnFocus: boolean;
    value: OptionType[];
    placeholder: string | undefined;
    handleFocus(): void;
    handleBlur(): void;
    onChange(selectedItem: Nullable<OptionType[]>, name?: string): void;
    onCreateOption(value: OptionType[], name?: string): void;
}

export function useMultiSelect({
    options,
    value,
    selectedValues,
    customValues,
    placeholder,
    title,
    forcePlaceholder,
    name,
    onChange,
    onCreateOption,
}: Params): Data {
    const { isOnFocus, handleFocus, handleBlur } = useCheckOnFocus();

    const baseValue = selectedValues ? makeValueFromSelected(options, selectedValues) : value;
    const customOptions = customValues?.map(makeCustomOption) ?? [];

    return {
        isOnFocus,
        value: [...(baseValue ?? []), ...customOptions],
        placeholder: resolvePlaceholder({ forcePlaceholder, placeholder, title, isOnFocus }),
        handleFocus,
        handleBlur,
        onChange: selectedItem => onChange?.(selectedItem, name),
        onCreateOption: createdValue => (onCreateOption ?? onChange)?.(createdValue, name),
    };
}
